<template>
  <div class="pb-4">
    <VouchersTable ref="table">
      <!-- Desktop : Export + Import + Créer dans la toolbar -->
      <template v-slot:actions="{ dataset }">
        <avs-button
          :data="dataset"
          color="accent"
          iconI="mdi-export"
          spantext="Exporter en excel"
          text
          :disabled="exporting"
          @click="exportToExcel"
        >
          <template v-slot:name>
            {{ exporting ? $t("Export...") : $t("Exporter") }}
          </template>
        </avs-button>

        <avs-button
          class="ml-2"
          color="primary"
          iconI="mdi-upload"
          spantext="Importer depuis Excel"
          text
          @click="importDialog = true"
        >
          <template v-slot:name>
            {{ $t("Importer") }}
          </template>
        </avs-button>

        <avs-createButton
          :to="{ name: 'VoucherCreateUpdate' }"
          :spantext="$t('Ajouter un nouveau bon')"
          class="ml-2"
          :name="$t('Nouveau Bon')"
        />
      </template>

      <!-- Mobile : Créer uniquement -->
      <template v-slot:actions-mobile="{ dataset }">
        <avs-createButton
          :data="dataset"
          :to="{ name: 'VoucherCreateUpdate' }"
          :spantext="$t('Ajouter un nouveau bon')"
          :name="$t('Nouveau Bon')"
        />
      </template>
    </VouchersTable>

    <ImportVoucherDialog v-model="importDialog" @imported="onImported" />
  </div>
</template>

<script>
import { VouchersTable } from "@/components";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { CompanyVouchersService } from "@/services";
import ImportVoucherDialog from "@/components/ri3/companyVouchers/ImportVoucherDialog.vue";
import { toDatetime } from "@/utils/datetime";

export default {
  name: "CompanyVoucherView",
  components: { VouchersTable, ImportVoucherDialog },
  data() {
    return {
      exporting: false,
      importDialog: false,
    };
  },
  methods: {
    onImported() {
      // refresh table après import
      const t = this._getTableInstance();
      if (typeof t?.fetchInitial === "function") t.fetchInitial();
      else if (typeof t?.fetchData === "function") t.fetchData();
    },

    // lecture des filtres : robuste (ref + fallback)
    _getTableInstance() {
      const t = this.$refs.table || this.$children?.[0];
      return t?.$refs?.table || t?.$refs?.inner || t?.$children?.[0] || t;
    },

    _getActiveFilters() {
      const t = this._getTableInstance();
      return {
        search: t?.search || "",
        start_date: t?.startDate || "",
        end_date: t?.endDate || "",
        shop: t?.selectedShop || "",
        product: t?.selectedProduct || "",
        vehicle: t?.selectedVehicle || "",
        department: t?.selectedDepartments || "",
        show_expired: !!t?.showExpired,
      };
    },

    async _fetchAllPages(filters) {
      const all = [];
      let page = 1;
      const MAX_PAGES = 10000;

      for (let i = 0; i < MAX_PAGES; i++) {
        const res = await CompanyVouchersService.get(this, { ...filters, page });

        const payload = res?.results ? res : { results: res };
        const rows = payload.results || [];
        if (!rows.length) break;

        all.push(...rows);

        if (payload.count && all.length >= payload.count) break;
        if (payload.next === null) break;

        page += 1;
      }

      const byId = new Map(all.map((v) => [v.id, v]));
      return Array.from(byId.values());
    },

    async exportToExcel() {
      if (this.exporting) return;
      this.exporting = true;

      try {
        const filters = this._getActiveFilters();
        const allData = await this._fetchAllPages(filters);

        if (!allData.length) {
          this.$store.commit("app/message", {
            msg: this.$t("messages.warning.no_data_to_export"),
            color: "warning",
          });
          return;
        }

        const exportData = allData.map((item) => ({
          Entreprise: item.recipient_company?.name || "N/A",
          Véhicule: item.vehicle_detail?.registration_number || "N/A",
          Conducteur: `${item.vehicle_detail?.driver_first_name || ""} ${item.vehicle_detail?.driver_last_name || ""}`.trim(),
          Montant: Number(item.purchase_amount) || 0,
          Litres: Number(item.purchase_liters) || 0,
          Restant: Number(item.remaining_amount) || 0,
          "Date de fin": toDatetime(item.valid_until) || "N/A",
          Site: item.company_shop?.name || "Tout le réseau",
          Produit: item.company_product?.name || "N/A",
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Bons");

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

        saveAs(
          new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
          `bons_${new Date().toISOString().slice(0, 10)}.xlsx`
        );

        this.$store.commit("app/message", {
          msg: this.$t("Fichier Excel exporté avec succès"),
          color: "success",
        });
      } catch (e) {
        this.$store.commit("app/message", {
          msg: this.$t("Une erreur s'est produite lors de l'export."),
          color: "error",
        });
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>
